// logcatTap.js — browser twin of proxy-server/scripts/android-prod-tap.js.
// Turns `adb logcat -s Ads -v time` text into request-only rows for
// POST /proxy/ingest/rows. The CLI script is the reference: every rule here
// (URL extraction, two-copy dedupe, truncation trim) must match it, and
// proxy-server/test/logcatTapParity.test.js runs both on the same input.
// Keep this file import-free so that test can load it under plain Node.

// Fallback only: the proxy serves the full list (incl. SDK-bidding buyer
// hosts from mediationNetworks.js) at GET /proxy/ingest/hosts.
export const BASE_AD_HOSTS = [
  'doubleclick.net',
  'googleads.g.doubleclick.net',
  'securepubads.g.doubleclick.net',
  'pubads.g.doubleclick.net',
  'googlesyndication.com',
  'admob.com',
  'amazon-adsystem.com',
];

export function extractUrls(line) {
  try {
    const m = String(line || '').match(/https?:\/\/[^\s"'<>\\]+/g) || [];
    return m.map((u) => u.replace(/[),.;\]}]+$/, '')).filter((u) => u.length > 8);
  } catch { return []; }
}

export function isAdHost(url, hosts = BASE_AD_HOSTS) {
  try {
    const h = new URL(url).hostname.toLowerCase();
    return hosts.some((d) => h === d || h.endsWith('.' + d));
  } catch { return false; }
}

export function extractAdUrls(line, { allUrls = false, hosts = BASE_AD_HOSTS } = {}) {
  const urls = extractUrls(line);
  return allUrls ? urls : urls.filter((u) => isAdHost(u, hosts));
}

// The GMA SDK logs each request twice, both cut at ~4KB, the shorter a prefix
// of the longer. Admit one; swap in the longer copy if it arrives second.
const DEDUPE_WINDOW_MS = 10000;
export function admitUrl(recent, url, now) {
  for (let i = recent.length - 1; i >= 0; i--) {
    if (now - recent[i].ts > DEDUPE_WINDOW_MS) recent.splice(i, 1);
  }
  for (const r of recent) {
    if (r.url === url && now - r.ts < 2000) return { action: 'drop' };
    if (r.url.startsWith(url)) return { action: 'drop' };
    if (url.startsWith(r.url)) return { action: 'upgrade', entry: r };
  }
  const entry = { url, ts: now, row: null };
  recent.push(entry);
  return { action: 'add', entry };
}

const TRUNCATION_MIN = 3500;
export function looksTruncated(line, url) {
  const l = String(line || '').replace(/\s+$/, '');
  return url.length >= TRUNCATION_MIN && l.endsWith(url);
}

export function trimToLastWholePair(url) {
  const q = url.indexOf('?');
  if (q < 0) return url;
  const cut = Math.max(url.lastIndexOf('&'), url.lastIndexOf('%26'));
  return cut > q ? url.slice(0, cut) : url;
}

// Stateful line -> row assembler. feed() takes raw decoded text (any chunking);
// take() hands back the rows not yet sent. Mirrors the CLI's onLine + batch.
// `adb logcat -v time` prefixes each line with the phone's clock: `10-07 17:00:24.123`.
// That is when the SDK logged the request; the time WE read the line is not: on
// connect logcat replays its whole backlog at once, so minutes of browsing would
// land within a second (fake duplicate bursts, wrong impression matching).
// No year in the prefix: assume the current one, and step back a year if that
// lands more than a day in the future (a backlog read just after New Year).
export function lineTime(line, now) {
  const m = /^(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})\.(\d{3})/.exec(String(line || '').trimStart());
  if (!m) return now;
  const d = new Date(now);
  let t = new Date(d.getFullYear(), +m[1] - 1, +m[2], +m[3], +m[4], +m[5], +m[6]).getTime();
  if (t - now > 86400000) t = new Date(d.getFullYear() - 1, +m[1] - 1, +m[2], +m[3], +m[4], +m[5], +m[6]).getTime();
  return Number.isFinite(t) ? t : now;
}

export function createLogcatTap({ deviceName = 'adb-webusb', hosts = BASE_AD_HOSTS, allUrls = false, now = () => Date.now() } = {}) {
  const recent = [];
  let batch = [];
  let buf = '';
  const stats = { seen: 0, deduped: 0, queued: 0 };

  const onLine = (line) => {
    for (const url of extractAdUrls(line, { allUrls, hosts })) {
      stats.seen++;
      const t = lineTime(line, now());
      const res = admitUrl(recent, url, t);
      const truncated = looksTruncated(line, url);
      const rowUrl = truncated ? trimToLastWholePair(url) : url;
      if (res.action === 'drop') { stats.deduped++; continue; }
      if (res.action === 'upgrade') {
        stats.deduped++;
        res.entry.url = url;
        if (res.entry.row && batch.includes(res.entry.row)) {
          res.entry.row.url = rowUrl;
          res.entry.row.truncated = truncated;
        }
        continue;
      }
      const row = { url: rowUrl, ts: t, method: 'GET', deviceName, source: 'logcat', truncated };
      res.entry.row = row;
      batch.push(row);
      stats.queued++;
    }
  };

  return {
    feed(text) {
      try {
        buf += text;
        const lines = buf.split('\n');
        buf = lines.pop();
        if (buf.length > 65536) buf = '';
        lines.forEach(onLine);
      } catch { /* never crash on odd logcat output */ }
    },
    // Flush the partial last line too (stream ended).
    end() { if (buf) { const l = buf; buf = ''; onLine(l); } },
    take() { const out = batch; batch = []; return out; },
    stats,
  };
}
